import React, { useState } from "react";
import { useProject } from "../features/projects/projectSlice";
import { useAppSelector } from "../app/hooks";
import { Col, Row, Form, Container, Stack } from "react-bootstrap";
import { ng } from "@common/ngds";

import { getValues, getUnitLabel, ValType, getSymbolLabelAsString, getLabel } from "../helpers/auxiliary";
import { useTranslation } from "react-i18next";
import { IUserSettings, Settings } from "../sharedTypes";
import DrawMtrlCurve, { MaterialRecord, MaterialCategory } from "./DrawMtrlCurve";
import { MaterialSvg } from "../helpers/mtrl/Material";
import SectionSVG from "./SectionSVG";
import { BoundingBox } from "../helpers/reinf/ReinfFun";
import { useUnit } from "../features/unit/unitSlice";

export const Ecm = () => (
  <>
    E<sub>cm</sub>
  </>
);
export const Fck = () => (
  <>
    f<sub>ck</sub>
  </>
);
export const Fctm = () => (
  <>
    f<sub>ctm</sub>
  </>
);
export const Fctk05 = () => (
  <>
    f<sub>ctk,0.05</sub>
  </>
);
export const Fctk95 = () => (
  <>
    f<sub>ctk,0.95</sub>
  </>
);
export const Eci = () => (
  <>
    E<sub>ci</sub>
  </>
);
export const Ecs = () => (
  <>
    E<sub>cs</sub>
  </>
);
export const Gc = () => (
  <>
    G<sub>c</sub>
  </>
);
export const Fckcube = () => (
  <>
    f<sub>ck,cube</sub>
  </>
);
export const Fcm = () => (
  <>
    f<sub>cm</sub>
  </>
);
export const Fctmcube = () => (
  <>
    f<sub>ctm,cube</sub>
  </>
);
export const Epsilonc1 = () => (
  <>
    &epsilon;<sub>c1</sub>
  </>
);
export const Epsiloncu1 = () => (
  <>
    &epsilon;<sub>cu1</sub>
  </>
);
export const Epsilonc2 = () => (
  <>
    &epsilon;<sub>c2</sub>
  </>
);
export const Epsiloncu2 = () => (
  <>
    &epsilon;<sub>cu2</sub>
  </>
);
export const Epsilonc3 = () => (
  <>
    &epsilon;<sub>c3</sub>
  </>
);
export const Epsiloncu3 = () => (
  <>
    &epsilon;<sub>cu3</sub>
  </>
);
export const Fctk = () => (
  <>
    f<sub>ctk</sub>
  </>
);
export const Fctmfl = () => (
  <>
    f<sub>ctm,fl</sub>
  </>
);
export const Nu = () => <>&nu;</>;
export const Rho = () => <>&rho;</>;
export const Alpha = () => <> &alpha; </>;
export const ypc = () => (
  <>
    <sub>y'</sub>
  </>
);
export const zpc = () => (
  <>
    <sub>z'</sub>
  </>
);
export const Iy = () => (
  <>
    I<sub>y'</sub>
  </>
);
export const Wy = () => (
  <>
    W<sub>y'</sub>
  </>
);
export const Iry = () => (
  <>
    ir<sub>y'</sub>
  </>
);
export const Smy = () => (
  <>
    Sm<sub>y'</sub>
  </>
);
export const Iz = () => (
  <>
    I<sub>z'</sub>
  </>
);
export const Wz = () => (
  <>
    W<sub>z'</sub>
  </>
);
export const Irz = () => (
  <>
    ir<sub>z'</sub>
  </>
);
export const Smz = () => (
  <>
    Sm<sub>z'</sub>
  </>
);
export const ang1 = () => (
  <>
    &alpha;<sub>1</sub>
  </>
);
export const c1 = () => (
  <>
    c<sub>1</sub>
  </>
);
export const SymbolDB = {
  concrete: {
    elasticityModulus: Ecm,
    characteristicCompressiveStrength: Fck,
    meanTensileStrength: Fctm,
    tensileStrength5Percentile: Fctk05,
    tensileStrength95Percentile: Fctk95,
    initialTangentialModulus: Eci,
    secantModulus: Ecs,
    shearModulus: Gc,
    cubeCompressiveStrength: Fckcube,
    meanCompressiveStrength: Fcm,
    meanTensileStrengthFlat: Fctmfl,
    strainAtPeakStress1: Epsilonc1,
    ultimateStrain1: Epsiloncu1,
    strainAtPeakStress2: Epsilonc2,
    ultimateStrain2: Epsiloncu2,
    strainAtPeakStress3: Epsilonc3,
    ultimateStrain3: Epsiloncu3,
    poissonRatio: Nu,
    density: Rho,
    compression: Fck,
    compressionMean: Fcm,
    tensionMean: Fctm,
    compressiveStrain2: Epsilonc2,
    ultimateCompressiveStrain2: Epsiloncu2,
    alpha: Alpha,
    ypc: ypc,
    zpc: zpc,
    Iy: Iy,
    Wy: Wy,
    iry: Iry,
    Smy: Smy,
    Iz: Iz,
    Wz: Wz,
    irz: Irz,
    Smz: Smz,
    ang1: ang1,
    c1: c1,
  },
};

function calcRot(rotVec: [number, number]) {
  let angle = Math.atan2(rotVec[1], rotVec[0]);
  angle *= 180 / Math.PI;
  if (angle < 0) {
    angle += 360;
  }
  return angle;
}

export const MaterialSectionDetail = () => {
  const { project: selProject } = useProject();
  const { t, i18n } = useTranslation();
  const {
    sectionalUnit,
    sectionalAreaUnit,
    sectionalBendResistanceUnit,
    sectionalStaticMomentUnit,
    sectionalMomentOfInertiaUnit,
    angleUnit,
    stressUnit,
    strainUnit,
    densityUnit,
    temperatureUnit,
    toDisplaySectionalLength: toDisplayLength,
    toDisplaySectionalArea: toDisplayArea,
    toDisplaySectionalMomentOfInertia: toDisplayMomOfInert,
    toDisplaySectionalBendResistance: toDisplayBendResist,
    toDisplaySectionalStaticMoment: toDisplayStatMom,
    toDisplayAngle,
    toDisplayStress,
    toDisplayStrain,
    toDisplayDensity,
  } = useUnit();
  const w = 400;
  const h = 400;
  const pad = 0.1;

  const [rotDeg, setRotDeg] = useState(() => {
    const deg = calcRot([0.0, 0.0]);
    return deg;
  });

  const mtrlSVG: MaterialSvg = {
    w: w,
    h: h,
    origo: { x: w * pad, y: h * (1 - pad) },
    max: { x: w * (1 - 2.5 * pad), y: h * (1 - 2.5 * pad) },
    pad: pad,
  };

  const symboldb = useAppSelector((state) => state.uiSlice.symbols);

  const mtrlGuid = selProject?.data.data?.input?.structures[0]?.elements[0].beam?.mtrlGuid ?? null;
  const mtrl = selProject?.data.data?.input?.mtrlDb.find((m) => m.id?.guid === mtrlGuid) ?? null;

  const beam = selProject?.data.data?.input?.structures[0]?.elements[0].beam;
  const secGuid = beam?.segments?.[0]?.layer?.start?.secGuid;
  const sec = secGuid
    ? (selProject?.data.data?.input?.secDb.find((section) => section.id?.guid === secGuid) ?? null)
    : null;
  const sectionDimensionsAndName =
    sec && mtrl
      ? calculateSectionWidthAndSectionheight(sec, mtrl)
      : { w: 0, h: 0, secName: "", mtrlname: "" };

  if (mtrl === null) {
    return <div>No material selected</div>;
  }
  if (sec === null) {
    return <div>No section selected</div>;
  }
  return (
    <Container fluid id="sx-table-container">
      <Row>
        <Col className="ps-0">
          <Stack className="sectionexplorer">
            {/* ---------- Header ---------- */}
            <Row>
              <Col className="mt-3">
                <span className="th-style" id="section-header">
                  {sectionDimensionsAndName.secName} {" - "} {sectionDimensionsAndName.mtrlname}
                </span>
              </Col>
              <hr style={{ width: "80%", marginTop: 0, marginBottom: 0, marginLeft: 10 }} />
            </Row>

            {/* ---------- SVG ---------- */}
            <Row className="mt-2">
              <Col>
                <div
                  className="gridbg box-shadow-1"
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "1px solid lightgray",
                  }}
                >
                  <SectionSVG
                    rotation
                    showAxes
                    section={sec}
                    zoomFactor={1}
                    rotVec={[0.0, 0.0]}
                    rotDeg={0.0}
                    setRotDeg={setRotDeg}
                  />
                </div>
              </Col>
            </Row>
          </Stack>
        </Col>

        {/* ========================= */}
        {/* SECTION DETAILS BLOCK     */}
        {/* ========================= */}
        <Col>
          {sec && (
            <Stack className="sectionexplorer p-0">
              <Col className="mt-3">
                {/* ---------- Header ---------- */}
                <Row>
                  <Col>
                    <span className="th-style">{t("section_details_.label")}</span>
                  </Col>
                  <hr style={{ width: "80%", marginTop: 0, marginBottom: 0, marginLeft: 0 }} />
                </Row>

                {/* ---------- Section Table ---------- */}
                <Container className="p-0 section-explorer-row-padding table-row-stripe mt-3">
                  {sec.units && sec.units.cog && (
                    <>
                      {"a" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">{t("section_a_.label")}</span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayArea(Number(sec.units.a) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalAreaUnit}
                          </Col>
                        </Row>
                      )}

                      <Row className="table-body">
                        <Col xs={4} className="border-none">
                          <span className="property-label">{t("section_w_.label")}</span>
                        </Col>
                        <Col className="border-none">
                          <span className="editfield-style">{toDisplayLength(sectionDimensionsAndName.w || 0)}</span>
                        </Col>
                        <Col xs={3} className="border-none">
                          {sectionalUnit}
                        </Col>
                      </Row>

                      <Row className="table-body">
                        <Col xs={4} className="border-none">
                          <span className="property-label">{t("section_h_.label")}</span>
                        </Col>
                        <Col className="border-none">
                          <span className="editfield-style">{toDisplayLength(sectionDimensionsAndName.h || 0)}</span>
                        </Col>
                        <Col xs={3} className="border-none">
                          {sectionalUnit}
                        </Col>
                      </Row>

                      {"x" in sec.units.cog && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">{t("section_cog_.label")}</span>
                            <span>{SymbolDB.concrete["ypc"]()}</span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayLength(Number(sec.units.cog.x) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalUnit}
                          </Col>
                        </Row>
                      )}

                      {"y" in sec.units.cog && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">{t("section_cog_.label")}</span>
                            <span>{SymbolDB.concrete["zpc"]()}</span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayLength(Number(sec.units.cog.y) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalUnit}
                          </Col>
                        </Row>
                      )}

                      {"iX" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["Iy"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayMomOfInert(Number(sec.units.iX) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalMomentOfInertiaUnit}
                          </Col>
                        </Row>
                      )}

                      {"wX" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["Wy"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayBendResist(Number(sec.units.wX) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalBendResistanceUnit}
                          </Col>
                        </Row>
                      )}

                      {"irX" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["iry"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayLength(Number(sec.units.irX) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalUnit}
                          </Col>
                        </Row>
                      )}

                      {"smX" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["Smy"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayStatMom(Number(sec.units.smX) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalStaticMomentUnit}
                          </Col>
                        </Row>
                      )}

                      {"iY" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["Iz"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayMomOfInert(Number(sec.units.iY) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalMomentOfInertiaUnit}
                          </Col>
                        </Row>
                      )}

                      {"wY" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["Wz"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayBendResist(Number(sec.units.wY) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalBendResistanceUnit}
                          </Col>
                        </Row>
                      )}

                      {"irY" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["irz"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayLength(Number(sec.units.irY) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalUnit}
                          </Col>
                        </Row>
                      )}

                      {"smY" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["Smz"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayStatMom(Number(sec.units.smY) || 0).toExponential(2)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {sectionalStaticMomentUnit}
                          </Col>
                        </Row>
                      )}

                      {"ang1" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["ang1"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">
                              {toDisplayAngle(Number(sec.units.ang1) || 0).toFixed(1)}
                            </span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {angleUnit}
                          </Col>
                        </Row>
                      )}

                      {"c1" in sec.units && (
                        <Row className="table-body">
                          <Col xs={4} className="border-none">
                            <span className="property-label">
                              <span>{SymbolDB.concrete["c1"]()}</span>
                            </span>
                          </Col>
                          <Col className="border-none">
                            <span className="editfield-style">{(Number(sec.units.c1) || 0).toFixed(2)}</span>
                          </Col>
                          <Col xs={3} className="border-none">
                            {" "}
                            {/* {getUnitLabel({
                              db: symboldb,
                              tag: "section_c1_",
                              style: "html",
                              settings: settings as any,
                            })} */}
                          </Col>
                        </Row>
                      )}
                    </>
                  )}
                </Container>
              </Col>
            </Stack>
          )}
        </Col>

        {/* ========================= */}
        {/* MATERIAL DETAILS BLOCK    */}
        {/* ========================= */}
        <Col>
          {mtrl && mtrl.conData && mtrl.conData.properties && (
            <Stack className="sectionexplorer p-0">
              <Col className="mt-3">
                {/* ---------- Header ---------- */}
                <Row>
                  <Col>
                    <span className="th-style">{t("material_details_.label")}</span>
                  </Col>
                  <hr style={{ width: "80%", marginTop: 0, marginBottom: 0, marginLeft: 10 }} />
                </Row>

                {/* ---------- Curve ---------- */}
                <Row className="mt-2">
                  <Col>
                    <DrawMtrlCurve mtrl={mtrl.conData.properties as MaterialRecord} svg={mtrlSVG} />
                  </Col>
                </Row>

                {/* ---------- Material Table ---------- */}
                <Container className="p-0 section-explorer-row-padding table-row-stripe mt-3">
                  {"elasticityModulus" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">
                          <Ecm />
                        </span>
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {toDisplayStress(Number(mtrl.conData.properties.elasticityModulus) || 0).toExponential(2)}
                        </span>
                      </Col>
                      <Col xs={3} className="border-none">
                        {stressUnit}
                      </Col>
                    </Row>
                  )}

                  {"compression" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">{SymbolDB.concrete["compression"]()}</span>
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {toDisplayStress(Number(mtrl.conData.properties.compression) || 0).toExponential(2)}
                        </span>
                      </Col>
                      <Col xs={3} className="border-none">
                        {stressUnit}
                      </Col>
                    </Row>
                  )}

                  {"compressionMean" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">{SymbolDB.concrete["compressionMean"]()}</span>
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {toDisplayStress(Number(mtrl.conData.properties.compressionMean) || 0).toExponential(2)}
                        </span>
                      </Col>
                      <Col xs={3} className="border-none">
                        {stressUnit}
                      </Col>
                    </Row>
                  )}

                  {"tensionMean" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">{SymbolDB.concrete["tensionMean"]()}</span>
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {toDisplayStress(Number(mtrl.conData.properties.tensionMean) || 0).toExponential(2)}
                        </span>
                      </Col>
                      <Col xs={3} className="border-none">
                        {stressUnit}
                      </Col>
                    </Row>
                  )}
                  {"compressiveStrain2" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">{SymbolDB.concrete["compressiveStrain2"]()}</span>{" "}
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {toDisplayStrain(Number(mtrl.conData.properties.compressiveStrain2) || 0).toExponential(2)}
                        </span>
                      </Col>

                      <Col xs={3} className="border-none">
                        {strainUnit}
                      </Col>
                    </Row>
                  )}

                  {"ultimateCompressiveStrain2" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">{SymbolDB.concrete["ultimateCompressiveStrain2"]()}</span>{" "}
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {toDisplayStrain(
                            Number(mtrl.conData.properties.ultimateCompressiveStrain2) || 0,
                          ).toExponential(2)}
                        </span>
                      </Col>

                      <Col xs={3} className="border-none">
                        {strainUnit}
                      </Col>
                    </Row>
                  )}

                  {"density" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">{SymbolDB.concrete["density"]()}</span>
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {toDisplayDensity(Number(mtrl.conData.properties.density) || 0).toExponential(2)}
                        </span>
                      </Col>
                      <Col xs={3} className="border-none">
                        {densityUnit}
                      </Col>
                    </Row>
                  )}

                  {"poissonsRatio" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">{SymbolDB.concrete["poissonRatio"]()}</span>
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {(Number(mtrl.conData.properties.poissonsRatio) || 0).toFixed(3)}
                        </span>
                      </Col>
                      <Col xs={3} className="border-none">
                        -
                      </Col>
                    </Row>
                  )}

                  {"alpha" in mtrl.conData.properties && (
                    <Row className="table-body">
                      <Col xs={4} className="border-none">
                        <span className="property-label">{SymbolDB.concrete["alpha"]()}</span>
                      </Col>
                      <Col className="border-none">
                        <span className="editfield-style">
                          {(Number(mtrl.conData.properties.alpha) || 0).toExponential(2)}
                        </span>
                      </Col>
                      <Col xs={3} className="border-none">
                        {`1/${temperatureUnit}`}
                      </Col>
                    </Row>
                  )}
                </Container>
              </Col>
            </Stack>
          )}
        </Col>
      </Row>
    </Container>
  );
};

const calculateSectionWidthAndSectionheight = (sec: ng.sections.Section_json, mtrl: ng.material.Data_json) => {
  let secW = 0;
  let secH = 0;
  const peri = sec.polylines?.perimeter;

  if (peri) {
    const pnts: ng.geometry.Point2D[] = [];

    for (const tmpPnt of peri.points ?? []) {
      const x = Number(tmpPnt.x);
      const y = Number(tmpPnt.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) {
        continue;
      }
      const pnt = ng.geometry.Point2D.create({
        x,
        y,
      });
      pnts.push(pnt);
    }

    if (pnts.length > 0) {
      const bb = BoundingBox(pnts);
      secW = Math.abs(bb.maxY - bb.minY);
      secH = Math.abs(bb.maxZ - bb.minZ);
    }
  }
  return { w: secW, h: secH, secName: sec.id?.name ?? "", mtrlname: mtrl.id?.name ?? "" };
};

// <div>
//   <Container id="sx-table-container">
//     <Row>
//       <Container>
//         <Row>
//           <Col className="text-center table-header-bg">
//             <span className="table-header-text">{t("name_.label")}</span>
//           </Col>
//           <Col className="text-center table-header-bg">
//             <span className="table-header-text">
//               {t("section_.label")}
//             </span>
//           </Col>

//           <Col className="text-center table-header-bg">
//             <span className="table-header-text">
//               {t("material_.label")}
//             </span>
//           </Col>
//         </Row>
//         <Row className="table-body align-items-center">
//           <Col className="text-center">
//             <Row style={{ marginTop: 7 }}>
//               <Col>
//                 <span style={{ fontWeight: "bold", fontSize: 12 }}>
//                   {sectionDimensionsAndName.secName} {" - "}{" "}
//                   {sectionDimensionsAndName.mtrlname}
//                 </span>
//               </Col>
//               <hr style={{ margin: "0px 0px 10px 0px" }} />
//             </Row>
//             <Row>
//               <div
//                 className="gridbg box-shadow-1"
//                 style={{
//                   display: "flex",
//                   justifyContent: "center",
//                   alignItems: "center",
//                   border: "1px solid lightgray",
//                 }}
//               >
//                 <SectionSVG
//                   rotation
//                   showAxes={true}
//                   section={sec}
//                   zoomFactor={1}
//                   rotVec={[0.0, 0.0]}
//                   rotDeg={0.0}
//                   setRotDeg={setRotDeg}
//                 />
//               </div>
//               <div>

//             {mtrl && mtrl.conData &&

//             <DrawMtrlCurve mtrl={mtrl.conData!.properties! as MaterialRecord} svg={mtrlSVG} />

//             }
//               </div>
//             </Row>
//           </Col>
//           <Col className="text-center">
//             {settings && sec && (
//               <>
//                 {"a" in sec.units! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {t("section_a_.label")}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       <span className="editfield-style">
//                         {getValues({
//                           val: sec.units!.a as ValType,
//                           db: symboldb,
//                           tag: "section_a_",
//                           settings: settings as any,
//                         })}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "section_a_",
//                         style: "html",
//                         settings: settings as any,
//                       })}
//                     </Col>{" "}
//                   </Row>
//                 )}

//                 <Row className="table-body">
//                   <Col className="border-none">
//                     <span className="property-label">
//                       {t("section_w_.label")}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {" "}
//                     <span className="editfield-style">
//                       {getValues({
//                         val: sectionDimensionsAndName.w as ValType,
//                         db: symboldb,
//                         tag: "section_w_",
//                         settings: settings as any,
//                       })}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {getUnitLabel({
//                       db: symboldb,
//                       tag: "section_w_",
//                       style: "html",
//                       settings: settings as any,
//                     })}
//                   </Col>
//                 </Row>

//                 <Row className="table-body">
//                   <Col className="border-none">
//                     <span className="property-label">
//                       {t("section_h_.label")}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {" "}
//                     <span className="editfield-style">
//                       {getValues({
//                         val: sectionDimensionsAndName.h as ValType,
//                         db: symboldb,
//                         tag: "section_h_",
//                         settings: settings as any,
//                       })}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {getUnitLabel({
//                       db: symboldb,
//                       tag: "section_h_",
//                       style: "html",
//                       settings: settings as any,
//                     })}
//                   </Col>
//                 </Row>

//                 {"cog" in sec.units! && (
//                   <>
//                     <Row className="table-body align-items-center justify-content-center">
//                       <Col
//                         className="border-none"
//                         style={{ paddingLeft: 5 }}
//                         lg={4}
//                       >
//                         <span className="property-label">
//                           <Row className="p-0 border-none">
//                             <Col className="border-none p-0">
//                               <span>{t("section_cog_.label")}</span>{" "}
//                               <span>{SymbolDB.concrete["ypc"]()}</span>
//                             </Col>
//                           </Row>
//                         </span>
//                       </Col>
//                       <Col className="border-none editfield-style">
//                         {getValues({
//                           val: sec.units!.cog?.x ?? 0,
//                           db: symboldb,
//                           tag: "section_cog_",
//                           settings: settings as any,
//                         })}
//                       </Col>
//                       <Col className="border-none">
//                         {getUnitLabel({
//                           db: symboldb,
//                           tag: "section_cog_",
//                           style: "html",
//                           settings: settings as any,
//                         })}
//                       </Col>
//                     </Row>

//                     <Row className="table-body align-items-center justify-content-center">
//                       <Col
//                         className="border-none"
//                         style={{ paddingLeft: 5 }}
//                         lg={4}
//                       >
//                         <span className="property-label">
//                           <Row className="p-0 border-none">
//                             <Col className="border-none p-0">
//                               <span> {t("section_cog_.label")}</span>{" "}
//                               <span>{SymbolDB.concrete["zpc"]()}</span>
//                             </Col>
//                           </Row>
//                         </span>
//                       </Col>
//                       <Col className="border-none editfield-style">
//                         {getValues({
//                           val: sec.units!.cog?.y ?? 0,
//                           db: symboldb,
//                           tag: "section_cog_",
//                           settings: settings as any,
//                         })}
//                       </Col>
//                       <Col className="border-none">
//                         {getUnitLabel({
//                           db: symboldb,
//                           tag: "section_cog_",
//                           style: "html",
//                           settings: settings as any,
//                         })}
//                       </Col>
//                     </Row>
//                   </>
//                 )}

//                 {/* //----------------- y - y ------------------- */}

//                 <Row className="table-body">
//                   <Col className="border-none">
//                     <span className="property-label">
//                       <span>{SymbolDB.concrete["Iy"]()}</span>
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     <span className="editfield-style">
//                       {getValues({
//                         val: sec.units!.iX as ValType,
//                         db: symboldb,
//                         tag: "section_i_yp_",
//                         settings: settings as any,
//                       })}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {getUnitLabel({
//                       db: symboldb,
//                       tag: "section_i_yp_",
//                       style: "html",
//                       settings: settings as any,
//                     })}
//                   </Col>{" "}
//                 </Row>

//                 <Row className="table-body">
//                   <Col className="border-none">
//                     <span className="property-label">
//                       {SymbolDB.concrete["Wy"]()}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     <span className="editfield-style">
//                       {getValues({
//                         val: sec.units!.wX as ValType,
//                         db: symboldb,
//                         tag: "section_w_yp_",
//                         settings: settings as any,
//                       })}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {getUnitLabel({
//                       db: symboldb,
//                       tag: "section_w_yp_",
//                       style: "html",
//                       settings: settings,
//                     })}
//                   </Col>{" "}
//                 </Row>

//                 <Row className="table-body">
//                   <Col className="border-none">
//                     <span className="property-label">
//                       {SymbolDB.concrete["ir"]()}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     <span className="editfield-style">
//                       {getValues({
//                         val: sec.units!.irX as ValType,
//                         db: symboldb,
//                         tag: "section_ir_yp_",
//                         settings: settings as any,
//                       })}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {getUnitLabel({
//                       db: symboldb,
//                       tag: "section_ir_yp_",
//                       style: "html",
//                       settings: settings as any,
//                     })}
//                   </Col>{" "}
//                 </Row>

//                 <Row className="table-body">
//                   <Col className="border-none">
//                     <span className="property-label">
//                       {SymbolDB.concrete["Smy"]()}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     <span className="editfield-style">
//                       {getValues({
//                         val: sec.units!.smX as ValType,
//                         db: symboldb,
//                         tag: "section_sm_yp_",
//                         settings: settings as any,
//                       })}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {getUnitLabel({
//                       db: symboldb,
//                       tag: "section_sm_yp_",
//                       style: "html",
//                       settings: settings,
//                     })}
//                   </Col>{" "}
//                 </Row>

//                 <Row className="table-body">
//                   <Col className="border-none">
//                     <span className="property-label">
//                       {SymbolDB.concrete["Iz"]()}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     <span className="editfield-style">
//                       {getValues({
//                         val: sec.units!.iY as ValType,
//                         db: symboldb,
//                         tag: "section_i_zp_",
//                         settings: settings as any,
//                       })}
//                     </span>
//                   </Col>
//                   <Col className="border-none">
//                     {" "}
//                     {getUnitLabel({
//                       db: symboldb,
//                       tag: "section_i_zp_",
//                       style: "html",
//                       settings: settings as any,
//                     })}
//                   </Col>
//                 </Row>

//                 {"wY" in sec.units! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["Wyz"]()}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {getValues({
//                           val: sec.units!.wY as any,
//                           db: symboldb,
//                           tag: "section_w_zp_",
//                           settings: settings as any,
//                         })}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "section_w_zp_",
//                         style: "html",
//                         settings: settings as any,
//                       })}
//                     </Col>{" "}
//                   </Row>
//                 )}

//                 {"ang1" in sec.units! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["ang1"]()}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {getValues({
//                           db: symboldb,
//                           val: sec.units!.ang1 as any,
//                           tag: "section_ang1_",
//                           settings: settings as any,
//                         })}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "section_ang1_",
//                         style: "html",
//                         settings: settings as any,
//                       })}
//                     </Col>{" "}
//                   </Row>
//                 )}

//                 {"c1" in sec.units! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["c1"]()}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {getValues({
//                           val: sec.units!.c1 as ValType,
//                           db: symboldb,
//                           tag: "section_c1_",
//                           settings: settings as any,
//                         })}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "section_c1_",
//                         style: "html",
//                         settings: settings as any,
//                       })}
//                     </Col>{" "}
//                   </Row>
//                 )}
//               </>
//             )}
//           </Col>
//           <Col className="text-center">
//             {/* //----------------- CONCRETE DETAILS ------------------- */}

//             {mtrl && mtrl.conData && (
//               <>
//                 {"elasticityModulus" in mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         <Ecm />
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {getValues({
//                           val: mtrl.conData!.properties!
//                             .elasticityModulus as ValType,
//                           db: symboldb,
//                           tag: "concreteproperties_elasticitymodulus_",
//                           settings: settings as any,
//                         })}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_elasticitymodulus_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}

//                 {"compression" in mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["compression"]()}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {" "}
//                         {mtrl.conData?.properties?.compression}{" "}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_compression_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}
//                 {"compressionMean" in mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["compressionMean"]()}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {" "}
//                         {mtrl.conData!.properties!.compressionMean}{" "}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_compressionmean_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}

//                 {"tensionMean" in mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["tensionMean"]()}
//                       </span>{" "}
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {" "}
//                         {mtrl.conData!.properties!.tensionMean}{" "}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_tensionmean_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}

//                 {"compressiveStrain2" in mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["compressiveStrain2"]()}
//                       </span>{" "}
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {mtrl.conData!.properties!.compressiveStrain2}
//                       </span>
//                     </Col>

//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_compressivestrain2_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}

//                 {"ultimateCompressiveStrain2" in
//                   mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete[
//                           "ultimateCompressiveStrain2"
//                         ]()}
//                       </span>{" "}
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {" "}
//                         {
//                           mtrl.conData!.properties!
//                             .ultimateCompressiveStrain2
//                         }
//                       </span>
//                     </Col>

//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_ultimatecompressivestrain2_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}

//                 {"density" in mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["density"]()}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       <span className="editfield-style">
//                         {mtrl.conData!.properties!.density}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_density_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}

//                 {"poissonsRatio" in mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["poissonRatio"]()}
//                       </span>{" "}
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {" "}
//                         {mtrl.conData!.properties!.poissonsRatio}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_poissonsratio_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}

//                 {"alpha" in mtrl.conData!.properties! && (
//                   <Row className="table-body">
//                     <Col className="border-none">
//                       <span className="property-label">
//                         {SymbolDB.concrete["alpha"]()}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       <span className="editfield-style">
//                         {" "}
//                         {mtrl.conData!.properties!.alpha}
//                       </span>
//                     </Col>
//                     <Col className="border-none">
//                       {" "}
//                       {getUnitLabel({
//                         db: symboldb,
//                         tag: "concreteproperties_alpha_",
//                         settings: settings,
//                         style: "html",
//                       })}
//                     </Col>
//                   </Row>
//                 )}
//               </>
//             )}
//           </Col>
//         </Row>
//       </Container>
//     </Row>
//   </Container>
// </div>
